import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { MileageModal } from '@/components/modals/MileageModal';
import { OfficersDesktopTable } from '@/components/screens/officers/OfficersDesktopTable';
import { OfficersMobileCards } from '@/components/screens/officers/OfficersMobileCards';

// ---------------------------------------------------------------------------
// Global declarations
// ---------------------------------------------------------------------------

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getOfficerLabel, currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { getOfficerPluralLabel } from '@/utils/auth';
import { ModuleType } from '@/constants/codes';
// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');
const processCallCardAvailableOfficers = _wg('processCallCardAvailableOfficers');
const getOfficerListFromCallCard = _wg('getOfficerListFromCallCard');
const convertOfficersForGrid = _wg('convertOfficersForGrid');


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Officer {
  uuid: string;
  id: number | null;
  officerId: number | null;
  name: string;
  isPrimary: boolean;
  dispatched: Date | null;
  enroute: Date | null;
  onScene: Date | null;
  cleared: Date | null;
  startingMileage: number | null;
  endingMileage: number | null;
  isBusy: boolean;
  isQueued: boolean;
  currentEventId: string | null;
  busyReason: string | null;
  eventId: string | null;
  callCardId: number | null;
}

export interface Person {
  id: number;
  name: string;
  company?: string;
  eventId?: string | null;
  callCard?: number | null;
  manualStatus?: number | null;
  [key: string]: any;
}

export interface OfficersAssignedProps {
  officers: Officer[];
  people?: Person[];
  title?: string;
  currentEventId?: string | null;
  moduleType?: string | number | null;
  isReadOnly?: boolean;
  onChange: (officers: Officer[]) => void;
  onDisabledFieldClick?: () => void;
}

export interface OfficersAssignedHandle {
  forceSaveAndCloseAllEdits: () => void;
}

interface EditingField {
  uuid: string;
  field: string;
}

interface DeleteConfirmState {
  open: boolean;
  uuid: string | null;
  name: string;
}

interface ReassignConfirmState {
  open: boolean;
  uuid: string | null;
  name: string;
  eventId: string | null;
  callCardId: number | null;
  officerId: number | null;
}

interface MileageModalState {
  open: boolean;
  uuid: string | null;
  name: string;
  startingMileage: number | null;
  endingMileage: number | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EDITABLE_FIELDS = ['name', 'dispatched', 'enroute', 'onScene', 'cleared'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OfficersAssigned = forwardRef<OfficersAssignedHandle, OfficersAssignedProps>(
  function OfficersAssigned(
    {
      officers,
      people = [],
      title: titleProp,
      currentEventId = null,
      moduleType = null,
      isReadOnly = false,
      onChange,
      onDisabledFieldClick,
    },
    ref
  ) {
    const { theme: currentTheme } = useTheme();

    // ---- labels ----
    const officerLabel = useMemo(() => getOfficerLabel(), []);
    const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);
    const title = titleProp ?? officerPluralLabel + ' Assigned';

    // ---- state ----
    const [editingField, setEditingField] = useState<EditingField | null>(null);
    const [editValue, setEditValue] = useState<any>('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerOpenDesktop, setDatePickerOpenDesktop] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [justSelected, setJustSelected] = useState(false);
    const [isTabbing, setIsTabbing] = useState(false);

    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
      open: false,
      uuid: null,
      name: '',
    });

    const [reassignConfirm, setReassignConfirm] = useState<ReassignConfirmState>({
      open: false,
      uuid: null,
      name: '',
      eventId: null,
      callCardId: null,
      officerId: null,
    });

    const [mileageModal, setMileageModal] = useState<MileageModalState>({
      open: false,
      uuid: null,
      name: '',
      startingMileage: null,
      endingMileage: null,
    });

    const [officerFilterPreference, setOfficerFilterPreference] = useState<boolean | null>(null);

    // ---- refs ----
    const rootRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedEditingFieldForTabRef = useRef<EditingField | null>(null);
    const isTabbingRef = useRef(false);
    const justSelectedRef = useRef(false);

    // Keep refs in sync with state for use in closures
    const editingFieldRef = useRef(editingField);
    const editValueRef = useRef(editValue);
    const officersRef = useRef(officers);
    const onChangeRef = useRef(onChange);
    const datePickerOpenRef = useRef(datePickerOpen);
    const datePickerOpenDesktopRef = useRef(datePickerOpenDesktop);
    const isDatePickerOpenRef = useRef(isDatePickerOpen);
    const peopleRef = useRef(people);

    useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
    useEffect(() => { editValueRef.current = editValue; }, [editValue]);
    useEffect(() => { officersRef.current = officers; }, [officers]);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
    useEffect(() => { datePickerOpenRef.current = datePickerOpen; }, [datePickerOpen]);
    useEffect(() => { datePickerOpenDesktopRef.current = datePickerOpenDesktop; }, [datePickerOpenDesktop]);
    useEffect(() => { isDatePickerOpenRef.current = isDatePickerOpen; }, [isDatePickerOpen]);
    useEffect(() => { justSelectedRef.current = justSelected; }, [justSelected]);
    useEffect(() => { isTabbingRef.current = isTabbing; }, [isTabbing]);
    useEffect(() => { peopleRef.current = people; }, [people]);

    // ---- computed ----
    const hasActiveCall = useMemo(() => !!currentEventId, [currentEventId]);

    const hasReassignPermission = useMemo(
      () => currentUserHasPermission(AccessAuthorities.ROLE_FORCE_ADD_OFFICER_TO_CALL_CARD),
      []
    );

    const datePickerPopupClass = currentTheme || '';

    // ---- helper: emit change ----
    const emitReplace = useCallback(
      (newArr: Officer[]) => {
        onChangeRef.current(newArr);
      },
      []
    );

    const emitMap = useCallback(
      (fn: (o: Officer) => Officer) => {
        emitReplace(officersRef.current.map(fn));
      },
      [emitReplace]
    );

    // ---- mobile detection ----
    const mobileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      const checkIfMobile = () => {
        if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
        mobileTimerRef.current = setTimeout(() => {
          setIsMobile(window.innerWidth <= 1024);
        }, 150);
      };
      checkIfMobile();
      window.addEventListener('resize', checkIfMobile);
      return () => {
        window.removeEventListener('resize', checkIfMobile);
        if (mobileTimerRef.current) clearTimeout(mobileTimerRef.current);
      };
    }, []);

    // ---- mount: ensure primary officer ----
    useEffect(() => {
      if (officers.length > 0 && !officers.some((o) => o.isPrimary)) {
        const next = officers.slice();
        if (next.length > 0 && !next[0].isBusy && !next[0].isQueued) {
          next[0] = { ...next[0], isPrimary: true };
          onChange(next);
        }
      }
      // Only run on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- listen for other datetime pickers opening ----
    useEffect(() => {
      const handleOtherPickerOpened = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        if (detail && detail.source !== 'officers-assigned') {
          setDatePickerOpen(false);
          setDatePickerOpenDesktop(false);
          setIsDatePickerOpen(false);
        }
      };
      window.addEventListener('datetime-picker-opened', handleOtherPickerOpened);
      return () => window.removeEventListener('datetime-picker-opened', handleOtherPickerOpened);
    }, []);

    // ---- load officer filter preference ----
    const loadOfficerFilterPreference = useCallback(() => {
      if (typeof api !== 'undefined' && api.getPersonSettings) {
        api.getPersonSettings(
          (result: any) => {
            if (
              result &&
              result.settings &&
              typeof result.settings.isOfficerFilterOnlineOnly === 'boolean'
            ) {
              setOfficerFilterPreference(result.settings.isOfficerFilterOnlineOnly);
            }
          },
          (_error: any) => {
            // Preference will load from localStorage in GroupedDropdownlist
          }
        );
      }
    }, []);

    useEffect(() => {
      loadOfficerFilterPreference();
    }, [loadOfficerFilterPreference]);

    // ---- date picker reposition on open ----
    const repositionDatePickerPopup = useCallback(() => {
      const doReposition = () => {
        const popup = document.querySelector('.datetime-picker-portal') as HTMLElement | null;
        const control =
          (document.querySelector('.edit-datetime .datetime-picker-control') as HTMLElement | null) ||
          (document.querySelector('.edit-datetime .datetime-picker-wrapper') as HTMLElement | null) ||
          (document.querySelector('.edit-datetime') as HTMLElement | null);

        if (popup && control) {
          const controlRect = control.getBoundingClientRect();

          // Skip repositioning if rect returns zeros
          if (controlRect.top === 0 && controlRect.bottom === 0) {
            return;
          }

          const popupHeight = popup.offsetHeight || 300;
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - controlRect.bottom;
          const spaceAbove = controlRect.top;

          popup.style.position = 'fixed';
          popup.style.zIndex = '9999';
          popup.style.bottom = '';

          if (spaceBelow >= popupHeight || spaceBelow >= spaceAbove) {
            popup.style.top = controlRect.bottom + 5 + 'px';
          } else {
            popup.style.top = (controlRect.top - popupHeight - 5) + 'px';
          }
        }
      };

      // Double rAF required: DateTimePicker positions its popup after the first tick
      requestAnimationFrame(() => {
        requestAnimationFrame(doReposition);
      });
    }, []);

    // Watch datePickerOpenDesktop for repositioning
    useEffect(() => {
      if (datePickerOpenDesktop) {
        repositionDatePickerPopup();
      }
    }, [datePickerOpenDesktop, repositionDatePickerPopup]);

    // Watch datePickerOpen for repositioning
    useEffect(() => {
      if (datePickerOpen) {
        repositionDatePickerPopup();
      }
    }, [datePickerOpen, repositionDatePickerPopup]);

    // ---- navigation helpers ----
    const getNextEditableField = useCallback(
      (currentUuid: string, currentField: string): EditingField | null => {
        const currentOfficerIndex = officersRef.current.findIndex((o) => o.uuid === currentUuid);
        const currentFieldIndex = EDITABLE_FIELDS.indexOf(currentField);
        const officer = officersRef.current[currentOfficerIndex];

        const allowedFields =
          officer?.isBusy || officer?.isQueued ? ['name'] : EDITABLE_FIELDS;

        // Find next field in current row
        for (let i = currentFieldIndex + 1; i < EDITABLE_FIELDS.length; i++) {
          if (allowedFields.indexOf(EDITABLE_FIELDS[i]) >= 0) {
            return { uuid: currentUuid, field: EDITABLE_FIELDS[i] };
          }
        }

        // Move to next row, first editable field
        if (currentOfficerIndex < officersRef.current.length - 1) {
          const nextOfficer = officersRef.current[currentOfficerIndex + 1];
          const nextAllowedFields =
            nextOfficer?.isBusy || nextOfficer?.isQueued ? ['name'] : EDITABLE_FIELDS;
          return { uuid: nextOfficer.uuid, field: nextAllowedFields[0] };
        }

        return null;
      },
      []
    );

    const getPreviousEditableField = useCallback(
      (currentUuid: string, currentField: string): EditingField | null => {
        const currentOfficerIndex = officersRef.current.findIndex((o) => o.uuid === currentUuid);
        const currentFieldIndex = EDITABLE_FIELDS.indexOf(currentField);
        const officer = officersRef.current[currentOfficerIndex];

        const allowedFields =
          officer?.isBusy || officer?.isQueued ? ['name'] : EDITABLE_FIELDS;

        // Find previous field in current row
        for (let i = currentFieldIndex - 1; i >= 0; i--) {
          if (allowedFields.indexOf(EDITABLE_FIELDS[i]) >= 0) {
            return { uuid: currentUuid, field: EDITABLE_FIELDS[i] };
          }
        }

        // Move to previous row, last editable field
        if (currentOfficerIndex > 0) {
          const prevOfficer = officersRef.current[currentOfficerIndex - 1];
          const prevAllowedFields =
            prevOfficer?.isBusy || prevOfficer?.isQueued ? ['name'] : EDITABLE_FIELDS;
          return { uuid: prevOfficer.uuid, field: prevAllowedFields[prevAllowedFields.length - 1] };
        }

        return null;
      },
      []
    );

    // ---- edit flow ----
    const closeEditMode = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setEditingField(null);
      setEditValue('');
      setDatePickerOpen(false);
      setDatePickerOpenDesktop(false);
      setIsDatePickerOpen(false);
    }, []);

    const saveEdit = useCallback(() => {
      const ef = editingFieldRef.current;
      if (!ef) return;
      const { uuid, field } = ef;
      const ev = editValueRef.current;

      if (field === 'name') {
        const selectedPerson = peopleRef.current.find((p: any) => p.id === ev);
        emitReplace(
          officersRef.current.map((o) =>
            o.uuid === uuid
              ? { ...o, officerId: ev, name: selectedPerson?.name || `New ${getOfficerLabel()}` }
              : o
          )
        );
      } else {
        emitReplace(
          officersRef.current.map((o) =>
            o.uuid === uuid ? { ...o, [field]: ev } : o
          )
        );
      }
    }, [emitReplace]);

    const cancelEdit = useCallback(() => {
      closeEditMode();
      savedEditingFieldForTabRef.current = null;
    }, [closeEditMode]);

    const clearDate = useCallback(() => {
      setEditValue(null);
      // Save and close synchronously after setting null
      // Use refs to get latest values
      setTimeout(() => {
        const ef = editingFieldRef.current;
        if (!ef) return;
        const { uuid, field } = ef;
        emitReplace(
          officersRef.current.map((o) =>
            o.uuid === uuid ? { ...o, [field]: null } : o
          )
        );
        closeEditMode();
      }, 0);
    }, [emitReplace, closeEditMode]);

    const startEdit = useCallback(
      (rowUuid: string, field: string, currentValue: any) => {
        if (isReadOnly) {
          if (typeof (window as any).App?.showPopupNotification === 'function') {
            (window as any).App.showPopupNotification('Edit mode required.', 'warning');
          }
          return;
        }

        const officer = officersRef.current.find((o) => o.uuid === rowUuid);
        if ((officer?.isBusy || officer?.isQueued) && field !== 'name') return;

        // If already editing something else, save and close it first
        if (editingFieldRef.current) {
          saveEdit();
          closeEditMode();
        }

        if (field === 'name') {
          setEditValue(officer?.officerId ?? null);
          setEditingField({ uuid: rowUuid, field });

          // Open the grouped select after render
          // The GroupedDropdownlist opens via its own toggle, so we just set editing mode
        } else {
          setEditValue(currentValue !== null ? currentValue : new Date());
          setEditingField({ uuid: rowUuid, field });

          requestAnimationFrame(() => {
            setDatePickerOpenDesktop(true);
          });
        }
      },
      [isReadOnly, saveEdit, closeEditMode]
    );

    const startEditMobile = useCallback(
      (rowUuid: string, field: string, currentValue: any) => {
        if (isReadOnly) {
          if (typeof (window as any).App?.showPopupNotification === 'function') {
            (window as any).App.showPopupNotification('Edit mode required.', 'warning');
          }
          return;
        }

        const officer = officersRef.current.find((o) => o.uuid === rowUuid);
        if ((officer?.isBusy || officer?.isQueued) && field !== 'name') return;

        // If already editing something else, save and close it first
        if (editingFieldRef.current) {
          saveEdit();
          closeEditMode();
        }

        if (field === 'name') {
          setEditValue(officer?.officerId ?? null);
          setEditingField({ uuid: rowUuid, field });
        } else {
          setEditValue(currentValue ?? new Date());
          setEditingField({ uuid: rowUuid, field });

          requestAnimationFrame(() => {
            setDatePickerOpen(true);
          });
        }
      },
      [isReadOnly, saveEdit, closeEditMode]
    );

    // ---- tab key handling ----
    const handleTabKey = useCallback(
      (event: any) => {
        if (!editingFieldRef.current) return;

        event.preventDefault();
        event.stopPropagation();

        isTabbingRef.current = true;
        setIsTabbing(true);
        const currentField = { ...editingFieldRef.current };
        const isShiftPressed = event.shiftKey;

        saveEdit();
        closeEditMode();

        // Wait for DOM to update after save
        requestAnimationFrame(() => {
          const next = isShiftPressed
            ? getPreviousEditableField(currentField.uuid, currentField.field)
            : getNextEditableField(currentField.uuid, currentField.field);

          if (next) {
            const officer = officersRef.current.find((o) => o.uuid === next.uuid);
            if (officer) {
              startEdit(next.uuid, next.field, (officer as any)[next.field]);
            }
          }

          isTabbingRef.current = false;
          setIsTabbing(false);
        });
      },
      [saveEdit, closeEditMode, getNextEditableField, getPreviousEditableField, startEdit]
    );

    const handleDropdownTab = useCallback(
      (tabEvent: { shiftKey: boolean }) => {
        if (!savedEditingFieldForTabRef.current) return;

        const currentField = { ...savedEditingFieldForTabRef.current };
        savedEditingFieldForTabRef.current = null;

        requestAnimationFrame(() => {
          const next = tabEvent.shiftKey
            ? getPreviousEditableField(currentField.uuid, currentField.field)
            : getNextEditableField(currentField.uuid, currentField.field);

          if (next) {
            const officer = officersRef.current.find((o) => o.uuid === next.uuid);
            if (officer) {
              startEdit(next.uuid, next.field, (officer as any)[next.field]);
            }
          }
        });
      },
      [getNextEditableField, getPreviousEditableField, startEdit]
    );

    // ---- officer selection ----
    const handleOfficerSelect = useCallback(
      (selectedItem: any) => {
        if (editingFieldRef.current) {
          savedEditingFieldForTabRef.current = { ...editingFieldRef.current };
        }

        justSelectedRef.current = true;
        setJustSelected(true);
        const selectedOfficerId = selectedItem?.id ?? null;
        setEditValue(selectedOfficerId);

        if (selectedOfficerId && editingFieldRef.current) {
          const uuid = editingFieldRef.current.uuid;
          const officerInfo = peopleRef.current.find((p: any) => p.id === selectedOfficerId);
          const isBusy = officerInfo?.eventId && officerInfo.eventId !== currentEventId;

          if (isBusy) {
            emitReplace(
              officersRef.current.map((o) =>
                o.uuid === uuid
                  ? {
                      ...o,
                      officerId: selectedOfficerId,
                      name: selectedItem?.name || `New ${officerLabel}`,
                      isPrimary: false,
                      isBusy: true,
                      isQueued: true,
                      eventId: officerInfo!.eventId!,
                      currentEventId: officerInfo!.eventId!,
                      callCardId: officerInfo!.callCard ?? null,
                      busyReason: 'BUSY',
                    }
                  : o
              )
            );
          } else {
            const currentOfficer = officersRef.current.find((o) => o.uuid === uuid);
            const wasPrimary = currentOfficer?.isPrimary || false;
            const shouldBePrimary =
              wasPrimary ||
              officersRef.current.length === 1 ||
              !officersRef.current.some(
                (o) => o.uuid !== uuid && o.isPrimary && !o.isBusy && !o.isQueued
              );

            emitReplace(
              officersRef.current.map((o) =>
                o.uuid === uuid
                  ? {
                      ...o,
                      officerId: selectedOfficerId,
                      name: selectedItem?.name || `New ${officerLabel}`,
                      isPrimary: shouldBePrimary,
                      isBusy: false,
                      isQueued: false,
                      eventId: null,
                      currentEventId: null,
                      callCardId: null,
                      busyReason: null,
                    }
                  : o
              )
            );
          }

          requestAnimationFrame(() => {
            closeEditMode();
          });
        } else {
          saveEdit();
        }
      },
      [currentEventId, officerLabel, emitReplace, closeEditMode, saveEdit]
    );

    const handleOfficerCancel = useCallback(() => {
      savedEditingFieldForTabRef.current = null;
      cancelEdit();
    }, [cancelEdit]);

    const handleDropdownClose = useCallback(() => {
      setTimeout(() => {
        if (
          editingFieldRef.current &&
          editingFieldRef.current.field === 'name' &&
          !justSelectedRef.current
        ) {
          cancelEdit();
        }
        justSelectedRef.current = false;
        setJustSelected(false);
        if (!justSelectedRef.current) {
          savedEditingFieldForTabRef.current = null;
        }
      }, 50);
    }, [cancelEdit]);

    const handleOfficerFilterChange = useCallback(
      (data: { isOfficerFilterOnlineOnly: boolean }) => {
        setOfficerFilterPreference(data.isOfficerFilterOnlineOnly);
      },
      []
    );

    // ---- date picker handlers ----
    const handleDateChange = useCallback((v: any) => {
      if (v === null || v === undefined || v === '') {
        setEditValue(null);
      } else {
        setEditValue(v);
      }
    }, []);

    const handleDatePickerOpen = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('datetime-picker-opened', { detail: { source: 'officers-assigned' } })
      );
      if (isMobile) {
        setIsDatePickerOpen(true);
        requestAnimationFrame(() => {
          const popup = document.querySelector('.datetime-picker-portal') as HTMLElement | null;
          const input = document.querySelector('.edit-datetime input') as HTMLElement | null;
          if (popup && input) {
            const inputRect = input.getBoundingClientRect();
            const popupHeight = popup.offsetHeight || 300;
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - inputRect.bottom;
            const spaceAbove = inputRect.top;

            popup.style.position = 'fixed';
            popup.style.zIndex = '9999';
            popup.style.bottom = '';

            if (spaceBelow >= popupHeight || spaceBelow >= spaceAbove) {
              popup.style.top = inputRect.bottom + 5 + 'px';
            } else {
              popup.style.top = (inputRect.top - popupHeight - 5) + 'px';
            }
          }
        });
      }
    }, [isMobile]);

    const handleEnteringTextMode = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }, []);

    const handleDatePickerClose = useCallback(() => {
      setIsDatePickerOpen(false);
      saveEdit();

      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        closeEditMode();
      }, 150);
    }, [saveEdit, closeEditMode]);

    const handleDatePickerOpenDesktop = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('datetime-picker-opened', { detail: { source: 'officers-assigned' } })
      );
      requestAnimationFrame(() => {
        repositionDatePickerPopup();
      });
    }, [repositionDatePickerPopup]);

    const handleDatePickerCloseDesktop = useCallback(() => {
      setDatePickerOpenDesktop(false);

      if (isTabbingRef.current) {
        return;
      }

      saveEdit();

      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        closeEditMode();
      }, 150);
    }, [saveEdit, closeEditMode]);

    const closeDatePicker = useCallback(() => {
      setDatePickerOpen(false);
      setDatePickerOpenDesktop(false);
      setIsDatePickerOpen(false);
      saveEdit();
      closeEditMode();
    }, [saveEdit, closeEditMode]);

    // ---- forceSaveAndCloseAllEdits ----
    const forceSaveAndCloseAllEdits = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      if (editingFieldRef.current) {
        const wasDatePickerOpen =
          datePickerOpenRef.current ||
          datePickerOpenDesktopRef.current ||
          isDatePickerOpenRef.current;

        setDatePickerOpen(false);
        setDatePickerOpenDesktop(false);
        setIsDatePickerOpen(false);

        if (wasDatePickerOpen) {
          requestAnimationFrame(() => {
            saveEdit();
            setEditingField(null);
            setEditValue('');
            savedEditingFieldForTabRef.current = null;
          });
        } else {
          saveEdit();
          setEditingField(null);
          setEditValue('');
          savedEditingFieldForTabRef.current = null;
        }
      }
    }, [saveEdit]);

    // Expose forceSaveAndCloseAllEdits via ref
    useImperativeHandle(
      ref,
      () => ({
        forceSaveAndCloseAllEdits,
      }),
      [forceSaveAndCloseAllEdits]
    );

    // Also register on window for external access
    useEffect(() => {
      (window as any).officersAssignedForceSave = forceSaveAndCloseAllEdits;
      return () => {
        delete (window as any).officersAssignedForceSave;
      };
    }, [forceSaveAndCloseAllEdits]);

    // ---- add officer ----
    const addOfficer = useCallback(() => {
      if (isReadOnly) {
        if (typeof (window as any).App?.showPopupNotification === 'function') {
          (window as any).App.showPopupNotification('Edit mode required.', 'warning');
        }
        return;
      }

      const isFirst = officers.length === 0;
      const hasPrimary = officers.some((o) => o.isPrimary);
      const newOfficer: Officer = {
        uuid: getInsecureUUID(),
        id: null,
        officerId: null,
        name: `Select ${officerLabel}`,
        isPrimary: isFirst || !hasPrimary,
        dispatched: new Date(),
        enroute: null,
        onScene: null,
        cleared: null,
        startingMileage: null,
        endingMileage: null,
        isBusy: false,
        isQueued: false,
        currentEventId: null,
        busyReason: null,
        eventId: null,
        callCardId: null,
      };
      onChange([...officers, newOfficer]);
      requestAnimationFrame(() => {
        if (rootRef.current) scrollToLastMobileCard(rootRef.current);
      });
    }, [isReadOnly, officers, officerLabel, onChange]);

    // ---- toggle primary ----
    const togglePrimary = useCallback(
      (uuid: string) => {
        if (isReadOnly) return;
        const officer = officers.find((o) => o.uuid === uuid);
        if (officer?.isBusy || officer?.isQueued) return;

        const wasPrimary = officer?.isPrimary;
        if (wasPrimary && officers.length === 1) return;

        onChange(officers.map((o) => ({ ...o, isPrimary: o.uuid === uuid })));
      },
      [isReadOnly, officers, onChange]
    );

    // ---- delete ----
    const openDeleteConfirm = useCallback(
      (uuid: string) => {
        if (isReadOnly) {
          if (typeof (window as any).App?.showPopupNotification === 'function') {
            (window as any).App.showPopupNotification('Edit mode required.', 'warning');
          }
          return;
        }

        const row = officers.find((o) => o.uuid === uuid);
        setDeleteConfirm({
          open: true,
          uuid,
          name: row?.name || `this ${officerLabel.toLowerCase()}`,
        });
      },
      [isReadOnly, officers, officerLabel]
    );

    const cancelDelete = useCallback(() => {
      setDeleteConfirm({ open: false, uuid: null, name: '' });
    }, []);

    const confirmDelete = useCallback(() => {
      const uuid = deleteConfirm.uuid;
      if (!uuid) return cancelDelete();

      const toRemove = officers.find((o) => o.uuid === uuid);
      const remaining = officers.filter((o) => o.uuid !== uuid);

      if (toRemove?.isPrimary && remaining.length) {
        const firstAvailable = remaining.find((o) => !o.isBusy && !o.isQueued);
        if (firstAvailable) {
          const idx = remaining.indexOf(firstAvailable);
          remaining[idx] = { ...remaining[idx], isPrimary: true };
        }
      }

      onChange(remaining);
      cancelDelete();
    }, [deleteConfirm.uuid, officers, onChange, cancelDelete]);

    // ---- reassign ----
    const openReassignConfirm = useCallback(
      (uuid: string) => {
        if (!hasActiveCall) {
          if (typeof (window as any).App?.showPopupNotification === 'function') {
            (window as any).App.showPopupNotification(
              `Send this call before reassigning a ${officerLabel.toLowerCase()}.`,
              'warning'
            );
          }
          return;
        }

        const officer = officers.find((o) => o.uuid === uuid);
        if (!officer) return;

        setReassignConfirm({
          open: true,
          uuid,
          name: officer.name,
          eventId: officer.eventId,
          callCardId: officer.callCardId,
          officerId: officer.officerId,
        });
      },
      [hasActiveCall, officers, officerLabel]
    );

    const cancelReassign = useCallback(() => {
      setReassignConfirm({
        open: false,
        uuid: null,
        name: '',
        eventId: null,
        callCardId: null,
        officerId: null,
      });
    }, []);

    const confirmReassign = useCallback(() => {
      const officerId = reassignConfirm.officerId;
      if (!officerId) return cancelReassign();

      api.forceOfficer(App.currentCC.id, officerId, false, (_result: any) => {
        api.getAvailableCADOfficers(App.currentCC.id, (availableOfficersResult: any) => {
          // Null check - User navigated to another screen
          if (!App.currentCCCntr) {
            cancelReassign();
            return;
          }

          processCallCardAvailableOfficers(availableOfficersResult);
          App.currentCCCntr.cachedOfficerList = availableOfficersResult.officerList;

          api.getCallCardById(App.currentCC.id, false, (callCardResult: any) => {
            const ccOfficers = getOfficerListFromCallCard(
              new (window as any).CallCard(callCardResult, ModuleType.cad, false)
            );
            const gridOfficers = convertOfficersForGrid(ccOfficers);
            onChangeRef.current(gridOfficers);

            App.showPopupNotification(`${officerLabel} reassigned successfully.`, 'success');
            cancelReassign();
          });
        });
      });
    }, [reassignConfirm.officerId, officerLabel, cancelReassign]);

    // ---- mileage ----
    const setMileage = useCallback(
      (uuid: string) => {
        if (isReadOnly) {
          if (typeof (window as any).App?.showPopupNotification === 'function') {
            (window as any).App.showPopupNotification('Edit mode required.', 'warning');
          }
          return;
        }

        const officer = officers.find((o) => o.uuid === uuid);
        if (!officer) return;

        setMileageModal({
          open: true,
          uuid,
          name: officer.name,
          startingMileage: officer.startingMileage ?? null,
          endingMileage: officer.endingMileage ?? null,
        });
      },
      [isReadOnly, officers]
    );

    const cancelMileage = useCallback(() => {
      setMileageModal({
        open: false,
        uuid: null,
        name: '',
        startingMileage: null,
        endingMileage: null,
      });
    }, []);

    const saveMileage = useCallback(
      (data: { startingMileage: number | null; endingMileage: number | null }) => {
        const uuid = mileageModal.uuid;
        if (!uuid) return cancelMileage();

        onChange(
          officers.map((o) =>
            o.uuid === uuid
              ? {
                  ...o,
                  startingMileage: data.startingMileage,
                  endingMileage: data.endingMileage,
                }
              : o
          )
        );
        cancelMileage();
      },
      [mileageModal.uuid, officers, onChange, cancelMileage]
    );

    // ---- URL builder ----
    const buildCallCardUrl = useCallback(
      (callCardId: number | null): string => {
        if (!callCardId) return '#';
        const mt = (moduleType + '').toUpperCase();
        if (mt === 'MDT') {
          return `/?sectionName=${mt}&tab=Search&function=MDT_ShowCallCard&multiModule=true&params=${callCardId}`;
        }
        return `/?sectionName=${mt || 'CAD'}&tab=Search&function=CAD_CallCard&multiModule=true&params=${callCardId}`;
      },
      [moduleType]
    );

    const buildReassignMessageHtml = useCallback((): string => {
      const eventId = reassignConfirm.eventId;
      const id = reassignConfirm.callCardId;
      if (!id)
        return `This ${officerLabel.toLowerCase()} is currently on another call.`;
      const href = buildCallCardUrl(id);
      return `This ${officerLabel.toLowerCase()} is currently on Call <a href="${href}" target="_blank" rel="noopener">#${eventId}</a>. Clear them from that call and reassign to this call?`;
    }, [reassignConfirm.eventId, reassignConfirm.callCardId, officerLabel, buildCallCardUrl]);

    // ---- disabled click ----
    const onDisabledClick = useCallback(() => {
      onDisabledFieldClick?.();
    }, [onDisabledFieldClick]);

    // ---- main render ----
    return (
      <div className={`officers-assigned${currentTheme ? ' ' + currentTheme : ''}`} ref={rootRef}>
        {/* Datepicker backdrop */}
        <div
          className={`datepicker-backdrop${isDatePickerOpen ? ' active' : ''}`}
          onClick={closeDatePicker}
        />

        {/* Read-only overlay */}
        {isReadOnly && <div className="disabled-overlay" onClick={onDisabledClick} />}

        {/* Header */}
        <div className="header">
          <h1>{title}</h1>
          <button
            className="btn-add"
            onClick={addOfficer}
            disabled={isReadOnly}
            title={
              isReadOnly ? 'Edit mode required' : `Add ${officerLabel.toLowerCase()}`
            }
          >
            <i className="fas fa-plus" /> {officerLabel.toUpperCase()}
          </button>
        </div>

        {/* Desktop table */}
        <OfficersDesktopTable
          officers={officers}
          people={people}
          officerLabel={officerLabel}
          officerPluralLabel={officerPluralLabel}
          isReadOnly={isReadOnly}
          editingField={editingField}
          editValue={editValue}
          datePickerOpenDesktop={datePickerOpenDesktop}
          datePickerPopupClass={datePickerPopupClass}
          officerFilterPreference={officerFilterPreference}
          hasReassignPermission={hasReassignPermission}
          hasActiveCall={hasActiveCall}
          autoOpenDropdown={!isMobile}
          onStartEdit={startEdit}
          onCancelEdit={cancelEdit}
          onTogglePrimary={togglePrimary}
          onDeleteConfirm={openDeleteConfirm}
          onReassignConfirm={openReassignConfirm}
          onSetMileage={setMileage}
          onOfficerSelect={handleOfficerSelect}
          onOfficerCancel={handleOfficerCancel}
          onDropdownTab={handleDropdownTab}
          onDropdownClose={handleDropdownClose}
          onOfficerFilterChange={handleOfficerFilterChange}
          onDateChange={handleDateChange}
          onDatePickerOpen={handleDatePickerOpenDesktop}
          onDatePickerClose={handleDatePickerCloseDesktop}
          onSaveEdit={saveEdit}
          onClearDate={clearDate}
          onTabKey={handleTabKey}
          buildCallCardUrl={buildCallCardUrl}
        />

        {/* Mobile cards */}
        <OfficersMobileCards
          officers={officers}
          people={people}
          officerLabel={officerLabel}
          officerPluralLabel={officerPluralLabel}
          isReadOnly={isReadOnly}
          editingField={editingField}
          editValue={editValue}
          datePickerOpen={datePickerOpen}
          datePickerPopupClass={datePickerPopupClass}
          officerFilterPreference={officerFilterPreference}
          hasReassignPermission={hasReassignPermission}
          hasActiveCall={hasActiveCall}
          autoOpenDropdown={isMobile}
          onStartEditMobile={startEditMobile}
          onCancelEdit={cancelEdit}
          onTogglePrimary={togglePrimary}
          onDeleteConfirm={openDeleteConfirm}
          onReassignConfirm={openReassignConfirm}
          onSetMileage={setMileage}
          onOfficerSelect={handleOfficerSelect}
          onOfficerCancel={handleOfficerCancel}
          onDropdownTab={handleDropdownTab}
          onDropdownClose={handleDropdownClose}
          onOfficerFilterChange={handleOfficerFilterChange}
          onDateChange={handleDateChange}
          onDatePickerOpen={handleDatePickerOpen}
          onDatePickerClose={handleDatePickerClose}
          onEnteringTextMode={handleEnteringTextMode}
          onSaveEdit={saveEdit}
          onClearDate={clearDate}
          onTabKey={handleTabKey}
          buildCallCardUrl={buildCallCardUrl}
        />

        {/* Delete confirm modal */}
        <ConfirmModal
          open={deleteConfirm.open}
          title={`Remove ${deleteConfirm.name}?`}
          confirmText="Remove"
          cancelText="Cancel"
          autoFocus="cancel"
          scrollLock={true}
          theme={currentTheme}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />

        {/* Reassign confirm modal */}
        <ConfirmModal
          open={reassignConfirm.open}
          title={`Reassign ${reassignConfirm.name}?`}
          messageHtml={buildReassignMessageHtml()}
          confirmText="Reassign"
          cancelText="Cancel"
          autoFocus="cancel"
          scrollLock={true}
          theme={currentTheme}
          onConfirm={confirmReassign}
          onCancel={cancelReassign}
        />

        {/* Mileage modal */}
        <MileageModal
          open={mileageModal.open}
          officerName={mileageModal.name}
          startingMileage={mileageModal.startingMileage}
          endingMileage={mileageModal.endingMileage}
          theme={currentTheme}
          onSave={saveMileage}
          onCancel={cancelMileage}
        />
      </div>
    );
  }
);

// ---- Window registration ----
(window as any).OfficersAssignedReact = OfficersAssigned;
